import { NavLink, useLocation } from "react-router-dom";
import {
  Search,
  Heart,
  MessageCircle,
  LayoutGrid,
  SparklesIcon,
} from "lucide-react";
import { useGetAccountSettings, useUnreads } from "@/tanstack-hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { REVIEWER_EMAILS } from "@/lib/utils";

function NotificationCircle({
  isActive = false,
  className = "",
  borderColorClass = "border-black",
}: {
  isActive?: boolean;
  className?: string;
  borderColorClass?: string;
}) {
  return (
    <span
      className={`absolute w-[0.58rem] h-[0.58rem] bg-[#CF0202] rounded-full 
        pointer-events-none border-[0.07rem]
        ${
          isActive
            ? `${borderColorClass} animate-bounce-custom`
            : borderColorClass
        }
        ${className}`}
    />
  );
}

export default function Menubar({ darkButtons }: { darkButtons?: boolean }) {
  const location = useLocation();
  const unreadsQuery = useUnreads();
  const unreads = unreadsQuery.data;
  const t = useTranslation();

  const accountSettingsQuery = useGetAccountSettings();
  const isReviewer = REVIEWER_EMAILS.includes(
    accountSettingsQuery.data?.email ?? "",
  );

  const menuItems = [
    {
      name: isReviewer ? t.MENU_BAR.AI_MATCHES : t.MENU_BAR.EXPLORE,
      path: "/",
      icon: isReviewer ? SparklesIcon : LayoutGrid,
    },
    { name: t.MENU_BAR.SEARCH, path: "/search", icon: Search },
    { name: t.MENU_BAR.FAVORITES, path: "/favorites", icon: Heart },
    { name: t.MENU_BAR.MESSAGES, path: "/messages", icon: MessageCircle },
  ];

  const handleTabClick = (path: string) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      sessionStorage.setItem(location.pathname, window.scrollY.toString());
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes bounceAnimation {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.2); }
            75% { transform: scale(0.95); }
          }
          @media (max-width: 767px) {
            .animate-bounce-custom {
              animation: bounceAnimation 0.4s ease-in-out;
            }
          }
          @keyframes bounceAnimationMd {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @media (min-width: 767px) {
            .animate-bounce-md {
              animation: bounceAnimationMd 0.2s ease-in-out;
            }
          }
        `}
      </style>
      <nav className="items-center text-white w-full select-none">
        <ul className="flex justify-between items-center mx-auto max-w-xl">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const heartNotification =
              item.name === t.MENU_BAR.FAVORITES &&
              (unreads?.favorite_unreads
                ? unreads.favorite_unreads > 0
                : false);

            const messageNotification =
              item.name === t.MENU_BAR.MESSAGES &&
              (unreads?.message_unreads ? unreads.message_unreads > 0 : false);

            return (
              <li key={item.name}>
                <NavLink
                  to={item.path}
                  onClick={() => handleTabClick(item.path)}
                  className={`flex flex-col md:flex-row items-center px-2 md:py-1.5 md:px-4 md:rounded-3xl ${
                    isActive
                      ? "md:bg-zinc-800 animate-bounce-md"
                      : darkButtons
                        ? "text-black"
                        : "text-gray-500"
                  }`}
                >
                  <span className="relative py-3 md:p-0">
                    <span
                      className={
                        isActive ? "animate-bounce-custom block" : "block"
                      }
                    >
                      <item.icon
                        className={`w-6 h-6 ${
                          isActive
                            ? "fill-white"
                            : darkButtons
                              ? "fill-black"
                              : ""
                        }`}
                      />
                    </span>
                    {heartNotification && (
                      <NotificationCircle
                        isActive={isActive}
                        className={`md:-top-0 md:-right-[0.05rem] ${
                          darkButtons
                            ? "top-[0.79rem] -right-[0.05rem] border-none w-[0.5rem] h-[0.5rem]"
                            : "top-3 -right-[0.1rem]"
                        }`}
                        borderColorClass={
                          isActive
                            ? "border-black md:border-zinc-800"
                            : "border-black"
                        }
                      />
                    )}
                    {messageNotification && (
                      <NotificationCircle
                        isActive={isActive}
                        className={`md:-top-0 md:-right-0 ${
                          darkButtons
                            ? "top-[0.78rem] right-[0.04rem] border-none w-[0.5rem] h-[0.5rem]"
                            : "top-3 -right-[0rem]"
                        }`}
                        borderColorClass={
                          isActive
                            ? "border-black md:border-zinc-800"
                            : "border-black"
                        }
                      />
                    )}
                  </span>
                  <span className="hidden md:block text-xs md:text-sm ml-2">
                    {item.name}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
